import { BehaviorSubject, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword, User } from 'firebase/auth';
import { SnackbarService } from './snackbar.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private sessionTokenSource: BehaviorSubject<string> = new BehaviorSubject<string>('null');
  private _loggedIn: BehaviorSubject<boolean>;
  private _activatedRoute: string;
  public showLoginLoader: Subject<boolean>;

  constructor(
    private router: Router,
    private snackbarService: SnackbarService
  ) {
    this._loggedIn = new BehaviorSubject<boolean>(null);
    this.showLoginLoader = new Subject<boolean>();
    this.sessionTokenSource = new BehaviorSubject<string>(null);
    this._activatedRoute = '';
    this.initAuthListener();
  }

  get sessionTokenRaw() {
    return this.sessionTokenSource.value;
  }

  get sessionToken$() {
    return this.sessionTokenSource.asObservable();
  }

  login(email: string, password: string): Promise<boolean | any> {
    const auth = getAuth();
    return new Promise((resolve, reject) => {
      signInWithEmailAndPassword(auth, email, password)
        .then((user) => {
          resolve(user);
        })
        .catch((e) => {
          reject(e);
        });
    });
  }

  initAuthListener() {
    try {
      const auth = getAuth();
      onAuthStateChanged(auth, async (user: User) => {
        this._activatedRoute = window.location.pathname;

        console.log('Auth state changed. User:', user);
        if (!!user && !user.isAnonymous) {
          const adminAccess = await this.initAdminAuth(user);
          user = adminAccess ? user : null;
          this.setLoggedInState(!!user);
          console.log('User is logged in:', user.email);
        }

        if (!!user && !!user.email) {
          this.setLoggedInState(true);
          if (this._activatedRoute === '/login') {
            this.router.navigate(['/home/members']);
          } else {
            this.router.navigate([this._activatedRoute]);
          }
        } else {
          this.setLoggedInState(false);
          this.router.navigateByUrl('/login');
        }
      });
    } catch (e) {
      console.error(e);
    }
  }

  async initAdminAuth(user: User) {
    if (!!user && !user.isAnonymous) {
      if (user?.uid) {
        const sessionToken = await user.getIdToken();
        this.sessionTokenSource.next(sessionToken);

        // Check if user has access to member portal (redundant check for safety)
        const idTokenResult = await user.getIdTokenResult();
        const portals = idTokenResult.claims['portals'] as string[];

        if (!portals || !portals.includes('admin')) {
          console.error('User does not have access to admin portal. Portals:', portals);
          this.showLoginLoader.next(false);
          this.snackbarService.showSnackbar(
            'Access denied. You do not have permission to access the admin portal.'
          );
          // Sign out without reload to allow form re-enable
          await this.logout();
          this.setLoggedInState(false);
          return false;
        }
      }
    }
    return true;
  }

  setLoggedInState(value: boolean) {
    this._loggedIn.next(value);
  }

  isLoggedIn$() {
    return this._loggedIn.asObservable();
  }

  logout() {
    this._loggedIn.next(false);
    return getAuth().signOut();
  }

  isLoggedIn() {
    return this._loggedIn.getValue();
  }
}
